import { create } from 'zustand';
import { ReactNode } from 'react';

export interface Dialog {
  id: string;
  title?: string;
  content: ReactNode;
  type?: 'info' | 'warning' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  closable?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
}

interface DialogStore {
  dialogs: Dialog[];
  open: (dialog: Omit<Dialog, 'id'>) => string;
  close: (id: string) => void;
  closeAll: () => void;
  confirm: (title: string, message: string, type?: Dialog['type']) => Promise<boolean>;
  alert: (title: string, message: string, type?: Dialog['type']) => Promise<void>;
}

export const useDialogs = create<DialogStore>((set, get) => ({
  dialogs: [],
  
  open: (dialog) => {
    const id = Date.now().toString();
    const newDialog = { ...dialog, id };
    set((state) => ({
      dialogs: [...state.dialogs, newDialog]
    }));
    return id;
  },
  
  close: (id) => set((state) => ({
    dialogs: state.dialogs.filter(d => d.id !== id)
  })),
  
  closeAll: () => set({ dialogs: [] }),
  
  confirm: (title, message, type = 'info') => {
    return new Promise<boolean>((resolve) => {
      const dialog: Omit<Dialog, 'id'> = {
        title,
        content: message,
        type,
        size: 'sm',
        showCloseButton: false,
        closable: false,
        onConfirm: () => {
          resolve(true);
          get().close(id);
        },
        onCancel: () => {
          resolve(false);
          get().close(id);
        },
        confirmText: 'Bestätigen',
        cancelText: 'Abbrechen'
      };
      const id = get().open(dialog);
    });
  },
  
  alert: (title, message, type = 'info') => {
    return new Promise<void>((resolve) => {
      const dialog: Omit<Dialog, 'id'> = {
        title,
        content: message,
        type,
        size: 'sm',
        showCloseButton: false,
        closable: false,
        onConfirm: () => {
          resolve();
          get().close(id);
        },
        confirmText: 'OK'
      };
      const id = get().open(dialog);
    });
  }
}));